import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faChevronRight, faPrint, faTags, faTrash, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { TransactionService } from '../../../../core/services/TransactionService';
import { PrinterDTO } from '../../../estimates/models/printer-dto';
import { InventoryBatchDTO } from '../../models/inventory-batch-dto';
import { InventoryBatchService } from '../../services/inventory-batch.service';

export interface InventoryBatchActionsDialogData {
  inventoryBatch: InventoryBatchDTO,
  closeDialogOnActions: boolean
}

export type InventoryBatchActionResult = 'reprint' | 'void';

type BatchActionMode = 'menu' | 'reprint' | 'void';

@Component({
  selector: 'inventory-batch-actions',
  templateUrl: './inventory-batch-actions.component.html',
  styleUrls: ['./inventory-batch-actions.component.scss']
})
export class InventoryBatchActionsComponent implements OnInit {

  static open(dialog: MatDialog, data: InventoryBatchActionsDialogData): MatDialogRef<InventoryBatchActionsComponent, InventoryBatchActionResult> {
    return dialog.open(InventoryBatchActionsComponent, { data, width: '480px', maxWidth: '95vw' });
  }

  public faChevronRight = faChevronRight;
  public faPrint = faPrint;
  public faTags = faTags;
  public faTrash = faTrash;
  public faTriangleExclamation = faTriangleExclamation;
  public faXmark = faXmark;

  public mode: BatchActionMode = 'menu';
  public printer: PrinterDTO;
  public isLoading = false;
  public reasonCodeId: number;
  public transactionReasons = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<InventoryBatchActionsComponent, InventoryBatchActionResult>,
    private batchService: InventoryBatchService,
    private errorService: ErrorService,
    private transactionService: TransactionService,
    @Inject(MAT_DIALOG_DATA) public data: InventoryBatchActionsDialogData) {}

  ngOnInit() {
    this.isLoading = true;
    this.transactionService.getTransactionReasonCodes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.transactionReasons = x;
      this.isLoading = false;
    }, error:err => { this.errorService.handleError(err); this.isLoading = false; }})
  }

  get detail() {
    return this.data?.inventoryBatch?.batchDetails?.[0];
  }

  get voidMessage(): string {
    const detail = this.detail;
    if (!detail)
      return 'This will remove every barcode in this batch from inventory.';

    const noun = detail.availableQuantity == 1 ? 'barcode' : 'barcodes';
    return `This will remove ${detail.availableQuantity} ${noun} for lot ${detail.lotNumber} from inventory.`;
  }

  setMode(mode: BatchActionMode) {
    this.mode = mode;
  }

  setPrinter(printer: PrinterDTO) {
    this.printer = printer;
  }

  reprintTags() {
    if (!this.printer?.id) return;

    this.isLoading = true;
    this.batchService.reprintBatch(this.data.inventoryBatch.id, this.printer.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:() => {
      this.isLoading = false;

      if (this.data.closeDialogOnActions)
        this.dialogRef.close('reprint');
      else
        this.mode = 'menu';
    }, error:err => { this.errorService.handleError(err); this.isLoading = false; }});
  }

  voidBatch() {
    if (!this.reasonCodeId) return;

    this.isLoading = true;
    this.batchService.voidBatch(this.data.inventoryBatch.id, this.reasonCodeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:() => {
      this.isLoading = false;
      this.dialogRef.close('void');
    }, error:err => { this.errorService.handleError(err); this.isLoading = false; }})
  }

  close() {
    this.dialogRef.close();
  }

}
